import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { format } from 'date-fns';
import { ErrorState } from '@/components/common/ErrorState';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { holidaysQueryOptions } from '../api/holidays.api';
import { HolidayDeleteDialog } from '../components/HolidayDeleteDialog';
import { HolidayFormDialog } from '../components/HolidayFormDialog';
import { HolidayMonthCalendar } from '../components/HolidayMonthCalendar';
import { HolidayMonthList } from '../components/HolidayMonthList';
import { HolidayMonthSummary } from '../components/HolidayMonthSummary';
import { HolidayMonthSwitcher } from '../components/HolidayMonthSwitcher';
import { MONTH_LABEL_FORMAT } from '../constants/holiday.constants';
import { useIsFlashing } from '../hooks/useIsFlashing';
import type { Holiday } from '../types/holiday.types';
import {
  getHolidaysInMonth,
  getMonthDayCounts,
  toMonthParam,
  toMonthStart,
} from '../utils/holidayCalendar';

const route = getRouteApi('/_auth/settings/holidays');

const PAGE_BODY_CLASS =
  'flex flex-col gap-4 lg:grid lg:min-h-0 lg:flex-1 lg:grid-cols-[minmax(0,1fr)_20rem] lg:grid-rows-[minmax(0,1fr)]';
const CALENDAR_CARD_CLASS =
  'flex flex-col overflow-hidden rounded-2xl bg-card shadow-sm ring-1 ring-primary/10 lg:max-h-156 lg:min-h-0';

interface HolidayFormState {
  holiday: Holiday | null;
  startDate: string | null;
}

export function HolidayPage() {
  const { month, date: openedDate } = route.useSearch();
  const navigate = route.useNavigate();
  const monthStart = useMemo(() => toMonthStart(month), [month]);
  const monthLabel = format(monthStart, MONTH_LABEL_FORMAT);
  const isOpenedDateFlashing = useIsFlashing(openedDate);

  const {
    data: holidays,
    error,
    refetch,
    isFetching,
  } = useQuery(holidaysQueryOptions(monthStart.getFullYear()));

  const monthHolidays = useMemo(
    () => (holidays ? getHolidaysInMonth(holidays, monthStart) : undefined),
    [holidays, monthStart],
  );
  const dayCounts = useMemo(
    () => (monthHolidays ? getMonthDayCounts(monthStart, monthHolidays) : undefined),
    [monthHolidays, monthStart],
  );

  const [formState, setFormState] = useState<HolidayFormState | null>(null);
  const [holidayToDelete, setHolidayToDelete] = useState<Holiday | null>(null);

  function changeMonth(nextMonthStart: Date) {
    void navigate({ search: { month: toMonthParam(nextMonthStart) }, replace: true });
  }

  function openAddForm(startDate: string | null) {
    setFormState({ holiday: null, startDate });
  }

  function openEditForm(holiday: Holiday) {
    setFormState({ holiday, startDate: null });
  }

  return (
    <>
      <div className={FIT_TO_SCREEN_PAGE_CLASS}>
        <PageHeader
          title="Holiday Calendar"
          actions={
            <HolidayMonthSwitcher
              monthStart={monthStart}
              monthLabel={monthLabel}
              onChangeMonth={changeMonth}
            />
          }
          createAction={{ label: 'Add Holiday', onClick: () => openAddForm(null) }}
        />

        {error ? (
          <ErrorState
            error={error}
            fallbackTitle="Could not load holidays"
            onRetry={() => void refetch()}
            isRetrying={isFetching}
            fill
          />
        ) : (
          <div className={PAGE_BODY_CLASS}>
            <div className={CALENDAR_CARD_CLASS}>
              <HolidayMonthSummary monthLabel={monthLabel} dayCounts={dayCounts} />
              <HolidayMonthCalendar
                monthStart={monthStart}
                holidays={monthHolidays}
                openedDate={openedDate}
                isOpenedDateFlashing={isOpenedDateFlashing}
                onAddHoliday={openAddForm}
                onEditHoliday={openEditForm}
              />
            </div>
            <HolidayMonthList
              monthLabel={monthLabel}
              holidays={monthHolidays}
              className="lg:max-h-full lg:min-h-0 lg:self-start"
              onEditHoliday={openEditForm}
              onDeleteHoliday={setHolidayToDelete}
            />
          </div>
        )}
      </div>

      <HolidayFormDialog
        holiday={formState?.holiday ?? null}
        startDate={formState?.startDate ?? null}
        open={formState !== null}
        onOpenChange={(open) => !open && setFormState(null)}
      />

      <HolidayDeleteDialog holiday={holidayToDelete} onClose={() => setHolidayToDelete(null)} />
    </>
  );
}
